'use client';

import {
  createContext, useContext, useEffect, useState,
} from 'react';
import { SIDEBAR_WIDTH_COLLAPSED } from './constants';

interface TwoColumnLayoutContextValue {
  isLeftOpen: boolean;
  toggleLeftSidebar: () => void;
}

export const TwoColumnLayoutContext = createContext<TwoColumnLayoutContextValue | undefined>(undefined);

const noop = () => {};

/**
 * Initial sidebar-open state for consumers that mount outside the layout's
 * React tree (e.g. NiceModal modals portaled to body that escape the page-level
 * `TwoColumnLayoutContext.Provider`). Derived from the `--two-column-header-offset`
 * CSS variable that `TwoColumnLayout` keeps in sync.
 *
 * Returns `null` when no layout is mounted at all, so the hook can distinguish
 * "outside layout entirely" (no offset to apply) from "inside layout, sidebar closed".
 */
const readInitialSidebarOpen = (): boolean | null => {
  if (typeof document === 'undefined') return null;
  const offset = document.documentElement.style.getPropertyValue('--two-column-header-offset').trim();
  if (!offset) return null;
  return offset !== SIDEBAR_WIDTH_COLLAPSED;
};

/**
 * @description Hook to access the TwoColumnLayout sidebar state.
 *
 * Returns the React context value when consumed inside the layout. When consumed
 * outside the layout (e.g. inside a NiceModal modal portaled above the layout's
 * provider) it falls back to a window-event subscription so consumers like
 * `CvDetailHub` can still reserve the sidebar offset.
 *
 * Returns `undefined` only when there is no `TwoColumnLayout` mounted anywhere.
 */
export const useTwoColumnLayout = (): TwoColumnLayoutContextValue | undefined => {
  const ctx = useContext(TwoColumnLayoutContext);
  const [fallbackOpen, setFallbackOpen] = useState<boolean | null>(() => readInitialSidebarOpen());

  useEffect(() => {
    if (ctx) return undefined;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ isOpen?: boolean }>).detail;
      if (detail && typeof detail.isOpen === 'boolean') {
        setFallbackOpen(detail.isOpen);
      }
    };
    window.addEventListener('sidebar-state-change', handler);
    return () => window.removeEventListener('sidebar-state-change', handler);
  }, [ctx]);

  if (ctx) return ctx;
  if (fallbackOpen === null) return undefined;
  return { isLeftOpen: fallbackOpen, toggleLeftSidebar: noop };
};
